import {
  AcknowledgeDTO,
  AiTaskStatus,
  JobRecommendTaskDTO,
  Message,
  MessageReadRequestDTO,
  ReadStatus,
  ReadStatusDTO,
  ResumeSummarizeTaskDTO,
  SenderType,
  UserStatus,
  UserStatusDTO,
} from "../models/Message";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchWithAuthentication, refreshToken } from "../utilities/FetchUtil";
import { ChatSession, MessagesResponse } from "../models/ChatSession";
import { useNavigate } from "react-router-dom";
import useStompClient, {
  StompClientValues,
  SubscriptionDetails,
} from "./useStompClient";
import { IMessage } from "@stomp/stompjs";
import {
  areSubscriptionsDifferent,
  createMessageAndDTO,
} from "../utilities/MessageUtil";
import { tokenUsername } from "../utilities/TokenUtil";

export interface MessageValues {
  sendMessage: (content: string, file: File | null) => void;
  getMessages: (isNewSession: boolean) => void;
  messages: Message[];
  pageNum: number;
  pageTotal: number;
  handleSubscription: (messageBody: Message) => void;
  messagesLoading: boolean;
  stompClientValues: StompClientValues;
  createSubscriptionDetails: (
    chatSessionList: ChatSession[] | null,
  ) => SubscriptionDetails[];
}

const useMessages = (
  activeSession: ChatSession | null,
  chatSessions: ChatSession[] | null,
  activeSessionIdRef: React.MutableRefObject<string | undefined>,
  incrementUnreadCount: (sessionId: string) => void,
  updateLastMessage: (sessionId: string, newMessage: string) => void,
  changeConnectionStatus: (status: string) => void,
  changeUserStatus: (newStatus: UserStatus, sessionId: string) => void,
  updateJobRecommendTaskStatus: (
    jobRecommendTaskDTO: JobRecommendTaskDTO,
  ) => void,
  updateResumeSummarizeTaskStatus: (
    newStatus: AiTaskStatus,
    taskId: string,
  ) => void,
  initialChatSessionsLoaded: boolean,
): MessageValues => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [pageNum, setPageNum] = useState<number>(1);
  const [pageTotal, setPageTotal] = useState<number>(1);
  const [messagesLoading, setMessagesLoading] = useState<boolean>(false);
  const [initialSubscriptionsCreated, setInitialSubscriptionsCreated] =
    useState(false);
  const [subscriptionDetailsList, setSubscriptionDetailsList] = useState<
    SubscriptionDetails[]
  >([]);

  const messagesRef = useRef(messages);

  const navigate = useNavigate();

  const stompClientValues = useStompClient(
    subscriptionDetailsList,
    changeConnectionStatus,
    initialSubscriptionsCreated,
  );

  const createSubscriptionDetails = (
    chatSessionList: ChatSession[] | null,
  ): SubscriptionDetails[] => {
    const newSubscriptions: SubscriptionDetails[] = [];

    if (chatSessionList == null || tokenUsername() === "superuser")
      return newSubscriptions;

    chatSessionList.forEach((chatSession) => {
      if (chatSession == null || chatSession.adminUserId == null) {
        return [];
      }

      const messageDestination = `/topic/to-admin/${chatSession.sessionId}`;
      const ackDestination = `/queue/ack/${chatSession.adminUserId}`;
      const userStatusDestination = `/queue/user-status/${chatSession.userId}`;
      const readStatusDestination = `/queue/message/status/${chatSession.sessionId}`;
      const jobRecommendStatusDestination = `/queue/ai-job-recommend/${chatSession.sessionId}`;
      const resumeSummarizeStatusDestination = `/queue/resume/${chatSession.sessionId}`;

      const messageCallback = (message: IMessage) => {
        const messageBody: Message = JSON.parse(message?.body);
        handleReceivedMessage(messageBody);
      };
      const ackCallback = (message: IMessage) => {
        const messageBody: AcknowledgeDTO = JSON.parse(message?.body);
        handleAck(messageBody, chatSession.sessionId);
      };
      const userStatusCallback = (message: IMessage) => {
        const messageBody: UserStatusDTO = JSON.parse(message?.body);
        handleUserStatusChange(messageBody, chatSession.sessionId);
      };
      const readStatusCallback = (message: IMessage) => {
        const messageBody: ReadStatusDTO = JSON.parse(message?.body);
        handleMessageRead(messageBody, chatSession.sessionId);
      };
      const jobRecommendStatusCallback = (message: IMessage) => {
        const messageBody: JobRecommendTaskDTO = JSON.parse(message?.body);
        handleJobRecommendTaskStatusChange(messageBody);
      };
      const resumeSummarizeStatusCallback = (message: IMessage) => {
        const messageBody: ResumeSummarizeTaskDTO = JSON.parse(message?.body);
        handleResumeSummarizeTaskStatusChange(
          messageBody,
          chatSession.sessionId,
        );
      };

      newSubscriptions.push(
        {
          destination: messageDestination,
          callback: messageCallback,
        },
        {
          destination: ackDestination,
          callback: ackCallback,
        },
        {
          destination: userStatusDestination,
          callback: userStatusCallback,
        },
        {
          destination: readStatusDestination,
          callback: readStatusCallback,
        },
        {
          destination: jobRecommendStatusDestination,
          callback: jobRecommendStatusCallback,
        },
        {
          destination: resumeSummarizeStatusDestination,
          callback: resumeSummarizeStatusCallback,
        },
      );
    });

    return newSubscriptions;
  };

  const markMessagesAsRead = useCallback(
    (messagesToMark: Message[]) => {
      if (tokenUsername() === "superuser") return messagesToMark;
      const readMessageIds: string[] = [];
      if (messagesToMark.length === 0) return messagesToMark;
      const markedMessages = messagesToMark.map((messageToMark) => {
        if (
          messageToMark.status === ReadStatus.UNREAD &&
          messageToMark.senderType === SenderType.USER
        ) {
          if (messageToMark.messageId) {
            readMessageIds.push(messageToMark.messageId);
          }
          return { ...messageToMark, status: ReadStatus.READ };
        }
        return messageToMark;
      });
      if (readMessageIds.length !== 0) {
        const messageReadRequest: MessageReadRequestDTO = {
          messageIds: readMessageIds,
        };
        stompClientValues.stompClient.publish({
          destination: "/app/message/read",
          body: JSON.stringify(messageReadRequest),
        });
      }
      return markedMessages;
    },
    [stompClientValues.stompClient],
  );

  const getMessages = useCallback(
    async (isNewSession: boolean) => {
      if (isNewSession) {
        setPageNum(1);
        setMessages([]);
      }
      if (activeSession == null) return;
      if (!isNewSession && pageNum > pageTotal) return;
      let response: Response;
      setMessagesLoading(true);
      try {
        response = await fetchWithAuthentication(
          `/chat/history/${activeSession.sessionId}?pageNum=${isNewSession ? 1 : pageNum}`,
          "GET",
          null,
        );
      } catch (error) {
        try {
          await refreshToken();
        } catch (error) {
          setMessagesLoading(false);
          console.log("Token expired, please login");
          navigate("/login");
          return error;
        }
        setMessagesLoading(false);
        return error;
      }
      if (!response.ok) {
        try {
          await refreshToken();
        } catch (error) {
          console.log("Token expired, please login");
          navigate("/login");
          setMessagesLoading(false);
          return;
        }
      }
      const body: MessagesResponse = await response.json();
      setPageTotal(body.pageTotal);
      const fetchedMessages = body.messages;
      fetchedMessages.sort(
        (a: Message, b: Message) =>
          new Date(a.isoCreatedAt).getTime() -
          new Date(b.isoCreatedAt).getTime(),
      );
      const markedMessages = markMessagesAsRead(fetchedMessages);
      if (messages.length === 0 || isNewSession) {
        setMessages(markedMessages);
      } else {
        setMessages((prev) => [...markedMessages, ...prev]);
      }
      setPageNum((prev) => prev + 1);
      setMessagesLoading(false);
    },
    [activeSession, pageNum, messages, navigate, pageTotal, markMessagesAsRead],
  );

  const sendMessage = async (content: string, file: File | null = null) => {
    if (activeSession == null || tokenUsername() === "superuser") {
      return;
    }
    try {
      const { newMessage, messageDTO } = await createMessageAndDTO(
        activeSession,
        content,
        file,
      );
      setMessages((prev) => [...prev, newMessage]);
      stompClientValues.stompClient.publish({
        destination: `/app/send-to-user/${activeSession.sessionId}`,
        body: JSON.stringify(messageDTO),
      });
    } catch (error) {
      refreshToken();
    }
  };

  /**
   * Subscription Callbacks
   */

  const handleReceivedMessage = (messageBody: Message) => {
    if (messageBody.sessionId === activeSessionIdRef.current) {
      markMessagesAsRead([messageBody]);
      messageBody.status = ReadStatus.READ;
      setMessages((prev) => [...prev, messageBody]);
    } else {
      incrementUnreadCount(messageBody.sessionId);
    }
    updateLastMessage(messageBody.sessionId, messageBody.content);
  };

  const handleMessageRead = (messageBody: ReadStatusDTO, sessionId: string) => {
    if (activeSessionIdRef.current === sessionId) {
      const updatedMessages: Message[] = messagesRef.current.map((message) => {
        if (message.messageId === messageBody.messageId) {
          return { ...message, status: messageBody.status };
        }
        return message;
      });
      setMessages(updatedMessages);
    }
  };

  const handleJobRecommendTaskStatusChange = (
    messageBody: JobRecommendTaskDTO,
  ) => {
    updateJobRecommendTaskStatus(messageBody);
  };

  const handleResumeSummarizeTaskStatusChange = (
    messageBody: ResumeSummarizeTaskDTO,
    sessionId: string,
  ) => {
    if (activeSessionIdRef.current === sessionId) {
      updateResumeSummarizeTaskStatus(
        messageBody.status,
        messageBody.resumeSummarizeTaskId,
      );
    }
  };

  const handleUserStatusChange = (
    messageBody: UserStatusDTO,
    sessionId: string,
  ) => {
    changeUserStatus(messageBody.status, sessionId);
  };

  const handleAck = (messageBody: AcknowledgeDTO, sessionId: string) => {
    if (activeSessionIdRef.current === sessionId) {
      const updatedMessages: Message[] = messagesRef.current.map((message) => {
        if (message.reqId === messageBody.reqId) {
          return { ...message, messageId: messageBody.messageId };
        }
        return message;
      });
      setMessages(updatedMessages);
    }
  };

  // Keep messages ref updated with any changes to messages
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Fetch messages from the active chat session
  useEffect(() => {
    const username = tokenUsername();
    if (
      (stompClientValues.clientConnected || username === "superuser") &&
      activeSession &&
      (stompClientValues.stompClient.active || username === "superuser")
    ) {
      getMessages(true)
        .then(() => console.log("Historic messages successfully fetched"))
        .catch(() => console.log("Historic messages failed to fetch"));
    }
    if (activeSession == null) {
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession, stompClientValues.clientConnected]);

  // Create subscriptions used in onConnect callback of stomp client
  useEffect(() => {
    if (chatSessions?.[0]?.adminUserId) {
      const newSubscriptionDetailsList =
        createSubscriptionDetails(chatSessions);
      if (
        areSubscriptionsDifferent(
          subscriptionDetailsList,
          newSubscriptionDetailsList,
        )
      ) {
        setSubscriptionDetailsList(newSubscriptionDetailsList);
        stompClientValues.unsubscribeFromAll();
        setInitialSubscriptionsCreated(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialChatSessionsLoaded]);

  return {
    sendMessage,
    getMessages,
    messages,
    pageNum,
    pageTotal,
    handleSubscription: handleReceivedMessage,
    messagesLoading,
    stompClientValues,
    createSubscriptionDetails,
  };
};

export default useMessages;
